import { html, render, nothing } from "lit-html";
import { IDetailedCartInfo } from "../../interfaces/detailed-cart-info";
import { DetailedCartController } from "../../detailed-cart-controller.js";
import { TotalSummary } from "../total-summary.js";
import { VippsHurtigkasse, PayWithVippsButtonSvgShape} from "../../../../checkout/vipps/cms-vipps-hurtigkasse.js";
import { OrderDiscountProgressBar } from "../../views/order-discount-progress-bar.js";
import { FreeShippingProgressBar } from "../../views/free-shipping-progress-bar.js";
import { OrderLinesTable } from "../order-lines-table.js";
import { DiscountCoupons } from "../discount-coupons.js";
import { cartEventSubscriber } from "../../../../common/pubsub/cart-event-pub-sub.js";
import { ProductStatusTextType } from "../../../../../components-shared/enums/product-status-text-type.js";
import { UmwAlert } from "../../../../common/umw-alert.js";
import { NS, createTranslator, initI18Next } from "../../../../../components-shared/utils/init-i18next.js";
import { Notify } from "../../../../../components-shared/utils/notify.js";

export enum CheckoutType {
    Private = "private",
    Company = "company",
    Both = "both"
}

export class DetailedCartV1 extends HTMLElement {

    constructor() {
        super();
    }

    /* #region Markup */
    protected template = () => {
        const orderLinesTable = new OrderLinesTable();
        const totalSummary = new TotalSummary();

        return html`
        ${this._detailedCartInfo.products.length === 0 ? html`
            <h1>${this.t("shoppingCart")}</h1>
            <div>${this.t("shoppingCartIsEmpty")}</div>
        ` : html `
        ${window.cartSettings.isCreateOfferEnabled ? html `<div class="alert alert-light" role="alert">Admin panel: <a href="#" class="alert-link create-offer-button" @click=${this._createOffer.bind(this)}>${this.t("btnCreateOffer")}</a></div>` : nothing}
        <h1>${this.t("shoppingCart")}</h1>
        ${orderLinesTable}
        <div class="remove-all-container d-flex justify-content-end">
            <button type="button" @click=${this._clearCart.bind(this)} class="btn btn-link">${this.t("btnRemoveAll")}</button>
        </div>
        ${this._warningBanner}
        <div class="discounts-and-total d-flex justify-content-end ${window.cartSettings.showCouponDiscounts ? "justify-content-between" : "justify-content-end"}">
            ${window.cartSettings.showCouponDiscounts ? new DiscountCoupons() : nothing }
            <div class="d-flex flex-column total-summary-and-action">
                ${totalSummary}
                ${this._freeShippingProgressBar}
                ${this._orderDiscountProgressBar}
                <div class="checkout-buttons ${this._checkoutButtonsLayout.toString()}">
                    ${this._checkoutButtonsLayout === CheckoutType.Private ? html `
                        <a href="${window.cartSettings.checkoutPageUrlB2C}" class="btn btn-lg btn-primary">${this.t("btnCheckout")}</a>
                        ${window.cartSettings.isVippsHurtigKasseEnabled ? this._vippsButton : nothing}
                    ` : nothing }
                    ${this._checkoutButtonsLayout === CheckoutType.Company ? html `
                        <a href="${window.cartSettings.checkoutPageUrlB2B}" class="btn btn-lg btn-primary">${this.t("btnCheckout")}</a>
                    ` : nothing }
                    ${this._checkoutButtonsLayout === CheckoutType.Both ? html `
                        <h3>${this.t("payAs")}</h3>
                        <a href="${window.cartSettings.checkoutPageUrlB2C}" class="btn btn-lg btn-primary">${this.t("btnPrivate")}</a>
                        ${window.cartSettings.isVippsHurtigKasseEnabled ? this._vippsButton : nothing}
                        <hr/>
                        <a href="${window.cartSettings.checkoutPageUrlB2B}" class="btn btn-lg btn-outline-primary">${this.t("btnCompanyInvoice")}</a>
                    ` : nothing }
                </div>
            </div>
        </div>`}
        `;
    }
    /* #endregion */

    /* #region Private */
    protected _detailedCartInfo: IDetailedCartInfo;
    protected _vippsButton = new VippsHurtigkasse(PayWithVippsButtonSvgShape.TextAndLogo);
    private _detailedCartController = new DetailedCartController();
    private _previousCartEmptyState: boolean = true;
    protected _freeShippingProgressBar = new FreeShippingProgressBar();
    protected _orderDiscountProgressBar = new OrderDiscountProgressBar();
    private _showOrderDiscountTooltip = false;
    private _warningBanner = new UmwAlert(window.globalSettings.overbuyEventSettings?.bannerText, false, "warning", "clock");
    private _showWarningBanner = window.globalSettings.overbuyEventSettings?.showOverbuyingMessage ?? false;
    private _notify = new Notify();
    private _isCheckoutClickHandlerAttached = false;
    private _checkoutButtonsLayout: CheckoutType = window.cartSettings.checkoutPageUrlB2C && window.cartSettings.checkoutPageUrlB2B ? CheckoutType.Both : window.cartSettings.checkoutPageUrlB2B ? CheckoutType.Company : CheckoutType.Private;

    protected _clearCart(){
        this._detailedCartController.clearCart();
    }

    protected _createOffer(event: Event){
        event.preventDefault();
        this._detailedCartController.createOffer();
    }

    private async _updateView(data: IDetailedCartInfo){
        this._detailedCartInfo = data;
        render(this.template(), this);

        if (!this._isCheckoutClickHandlerAttached) this._bindCheckoutButtonClick();
    }

    private async _onDetailedCartChanged(topic: string, data: IDetailedCartInfo){
        this._freeShippingProgressBar.setData(data.freeShippingInfo);
        this._showOrderDiscountTooltip = this._detailedCartController.getOrderDiscountVisibility();
        this._orderDiscountProgressBar.setVisibility(this._showOrderDiscountTooltip);

        if (this._hasCartStateChanged(data)){
            this._updateView(data);
        }

        this._detailedCartInfo = data;

        this._warningBanner.visible = this._showWarningBanner && data.products.some(p => p.productStatusTextType == ProductStatusTextType.OrderingProduct && p.quantity > p.quantityInStock);
    }

    private _hasCartStateChanged(newCartInfo: IDetailedCartInfo): boolean {
        const newCartEmptyState = this._isCartEmpty(newCartInfo);
        if (this._previousCartEmptyState !== newCartEmptyState) {
            this._previousCartEmptyState = newCartEmptyState;
            return true;
        }
        return false;
    }

    private _isCartEmpty(data: IDetailedCartInfo): boolean {
        return data?.summary.diffProductCount === 0;
    }

    private _onCheckoutButtonClick(event: MouseEvent) {
        const target = event.target as HTMLElement;
        const link = target.closest("a.btn");
        if (!link) return;

        const shouldProceed = this._validateBeforeCheckout();
        if (!shouldProceed) {
            event.preventDefault();
            event.stopPropagation();
        }
    }

    private _validateBeforeCheckout(): boolean {
        if (!this._detailedCartInfo.summary.sumAboveMinimum) {
            const errorMessage = this.t("minOrderSumError").replace('[MinOrderSum]', window.cartSettings.formattedMinOrderSum);
            this._notify.error(errorMessage);
            return false;
        }

        return true;
    }

    private _bindCheckoutButtonClick() {
        const checkoutButtonsContainer = this.querySelector(".checkout-buttons");
        if (checkoutButtonsContainer) {
            checkoutButtonsContainer.addEventListener("click", this._onCheckoutButtonClick.bind(this));
            this._isCheckoutClickHandlerAttached = true;
        }
    }

    protected t = createTranslator(NS.PUBLIC_ESTORE_DETAILED_CART);

    async connectedCallback() {
        await initI18Next(NS.PUBLIC_ESTORE_DETAILED_CART);

        if(this.isConnected){
            cartEventSubscriber.onDetailedCartChanged(this._onDetailedCartChanged.bind(this));
        }
    }
    /* #endregion */
}

customElements.define('detailed-cart-v1', DetailedCartV1);
